import type { DocumentsListing } from '@contracts/document/DocumentResponse';
import Alert from '@mui/material/Alert';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Checkbox } from '@src/common/components/Checkbox';
import { IconButton } from '@src/common/components/IconButton';
import { Stack } from '@src/common/components/Stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Typography } from '@src/common/components/Typography';
import { ViewableDocumentShortcut } from '@src/common/components/ViewableDocumentShortcut';
import { DownloadService } from '@src/common/services/DownloadService';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import { RenamingOption } from '@src/documents/components/RenamingOption';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import { Download01 } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  documents: DocumentsListing[];
  readonly?: boolean;
  onCheck?: ({ checked, id }: { checked: boolean; id?: string }) => void;
  defaultChecked?: string[];
  readonlyChecked?: string[];
}

export const DocumentTable = ({
  documents,
  readonly,
  onCheck,
  defaultChecked,
  readonlyChecked,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const showCheckbox = !isClientUser(user);

  return (
    <Stack gap={theme.spacing['8']}>
      {!readonly && showCheckbox && (
        <Alert severity="warning">{t('displayFileToClientWarning')}</Alert>
      )}
      <Table stickyHeader maxHeight="60vh">
        <TableHead>
          <TableRow>
            {showCheckbox && <TableCell></TableCell>}
            <TableCell>{t('documents.name')}</TableCell>
            <TableCell>{t('documents.permitInfo')}</TableCell>
            <TableCell>{t('documents.date')}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((document) => (
            <Row
              key={document.id}
              row={document}
              readonly={readonly}
              onCheck={onCheck}
              showCheckbox={showCheckbox}
              defaultChecked={defaultChecked}
              readonlyChecked={readonlyChecked}
            />
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

const download = async (documentId: string) => {
  const { blob, filename } = await DocumentsClient.downloadWithId(documentId);
  void DownloadService.download(blob, filename);
};

const Row = ({
  row,
  readonly,
  onCheck,
  showCheckbox,
  defaultChecked,
  readonlyChecked,
}: {
  row: DocumentsListing;
  readonly?: boolean;
  onCheck?: ({ checked, id }: { checked: boolean; id?: string }) => void;
  showCheckbox: boolean;
  defaultChecked?: string[];
  readonlyChecked?: string[];
}) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(row.displayName);
  const [checked, setChecked] = useState(
    defaultChecked?.includes(row.id) ?? false,
  );
  const isReadonlyChecked = readonlyChecked?.includes(row.id) ?? false;

  const downloadDocument = () => download(row.id);
  const highlightPermitAction = (text: string) => {
    const regex = /(\(R\)|\(A\)|\(M\))/g;

    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <Typography component="span" key={index} style={{ fontWeight: 600 }}>
          {part}
        </Typography>
      ) : (
        <Typography component="span" key={index}>
          {part}
        </Typography>
      ),
    );
  };

  const getName = () => {
    return displayName?.trim().length
      ? displayName
      : getLastPartOfPath(row.key);
  };
  const onClose = (newName?: string) => {
    setDisplayName(newName);
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setChecked(isChecked);
    if (onCheck) {
      onCheck({ id: row.id, checked: isChecked });
    }
  };

  return (
    <TableRow>
      {showCheckbox && (
        <TableCell>
          <Checkbox
            checked={checked}
            onChange={handleCheckChange}
            label=""
            disabled={readonly || isReadonlyChecked}
          />
        </TableCell>
      )}
      <TableCell>
        <Stack direction="row" gap={theme.spacing['2']}>
          <ViewableDocumentShortcut document={row} fontSize="medium" />
          <Typography>{getName()}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography>{highlightPermitAction(row.parent.name)}</Typography>
      </TableCell>
      <TableCell>
        <Typography>
          {Timestamp.from(row.createdAt).formatDate('dd/MM/yyyy h:mm a')}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={theme.spacing['1']}>
          <IconButton
            onClick={downloadDocument}
            aria-label={t('documents.download')}
          >
            <Download01 />
          </IconButton>
          {!readonly && (
            <RenamingOption
              documentId={row.id}
              currentName={getName()}
              onSubmit={onClose}
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};
